import GrabPayWidget from "@components/commerce/product/grab-pay-widget/GrabPayWidget";
import { activeSiteConfig } from "@config/site/site-config";
import type { ProductPrice } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import { ExtraPriceType } from "@lib/constants/filtersConstants";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CustomAttributes, EXTRA_PRICE_CONFIG } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { isProductSalePriceHighlighted } from "@lib/utils/productUtility";
import { Text } from "@ecco/ui/text";
import { Money } from "@ecco/ui/money";
import type { SimpleMoneyData } from "@ecco/ui/use-money";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { useLocale } from "next-intl";

interface ProductBuyCardPriceProps {
    price: ProductPrice;
    productId: string;
}

const ProductBuyCardPrice = ({ price, productId }: ProductBuyCardPriceProps) => {
    const locale = useLocale();
    const { get } = useMicrocopy();

    const customFields = price?.custom?.customFieldsRaw;
    const currentPrice = price?.value;
    const discountPercentage = getProductAttributeValue(
        customFields,
        CustomAttributes.DISCOUNT_PERCENTAGE
    ) as number;
    const isDiscounted = discountPercentage > 0;

    const showGrabPay = activeSiteConfig?.hasGrabPayWidget;
    const taxLabel =
        activeSiteConfig?.includesAllTaxes && get(CommonCMS.page, CommonCMS.price.includesTaxes);

    // EARLY RETURN (NOT DISCOUNTED)
    if (!isDiscounted) {
        return (
            <Box mt={1}>
                <Flex direction="column" gap={2}>
                    <Flex direction="column" align="flex-start" gap={1.2}>
                        <Money
                            fontWeight="bold"
                            locale={locale}
                            data={currentPrice}
                            color="black"
                            fontSize="desktopH3"
                            data-testid="SelectedVariantPrice"
                        />

                        {showGrabPay && (
                            <GrabPayWidget
                                productPrice={currentPrice}
                                locale={locale}
                                productId={productId}
                            />
                        )}
                    </Flex>

                    {taxLabel && <Text color="gray.800">{taxLabel}</Text>}
                </Flex>
            </Box>
        );
    }

    const extraPriceType: ExtraPriceType[] = activeSiteConfig?.extraPriceType || [];
    const isOmnibusMarket = extraPriceType?.includes(ExtraPriceType.OMNIBUS) ?? false;

    const recommendedPrice = getProductAttributeValue(
        customFields,
        CustomAttributes.RECOMMENDED_PRICE
    ) as SimpleMoneyData | undefined;

    const previousLowestPrice = isOmnibusMarket
        ? (getProductAttributeValue(customFields, CustomAttributes.PREVIOUS_LOWEST_PRICE) as
              | SimpleMoneyData
              | undefined)
        : undefined;

    const previousLowestDiscount = getProductAttributeValue(
        customFields,
        CustomAttributes.DISCOUNT_PERCENTAGE_PREVIOUS_LOWEST_PRICE
    );
    const discountPercentagePreviousLowest =
        isOmnibusMarket && typeof previousLowestDiscount === "number"
            ? previousLowestDiscount
            : undefined;

    // If Omnibus market AND omnibus price missing → use RRP + normal discount
    const effectiveOmnibusPrice =
        isOmnibusMarket && !previousLowestPrice?.centAmount
            ? recommendedPrice
            : previousLowestPrice;
    const effectiveOmnibusDiscountPercentage =
        isOmnibusMarket && !previousLowestPrice?.centAmount
            ? discountPercentage
            : discountPercentagePreviousLowest;

    // EXTRA PRICES TO DISPLAY LOGIC (Recommended, Adjusted, Omnibus)
    const extraPrices = extraPriceType
        .map((type) => {
            const config = EXTRA_PRICE_CONFIG[type];

            const value: SimpleMoneyData | undefined =
                type === ExtraPriceType.OMNIBUS
                    ? effectiveOmnibusPrice
                    : (getProductAttributeValue(customFields, config.attribute) as SimpleMoneyData);

            let discount: number | undefined;

            if (type === ExtraPriceType.OMNIBUS) {
                // A 0% Omnibus discount can sit next to a price only higher by rounding decimals.
                if ((effectiveOmnibusDiscountPercentage ?? 0) <= 0) {
                    return null;
                }
                discount = effectiveOmnibusDiscountPercentage;
            } else if (type === ExtraPriceType.RRP && isOmnibusMarket) {
                // 🚫 Skip RRP if omnibus price is higher than current price
                if (
                    typeof effectiveOmnibusDiscountPercentage !== "number" ||
                    effectiveOmnibusDiscountPercentage <= 0
                ) {
                    return null;
                }
                discount = discountPercentage ?? undefined;
            }

            return { type, value, discount, key: config.key, setKey: config.setKey };
        })
        .filter(Boolean)
        .filter(({ value }) => {
            if (!value?.centAmount) return false;

            // Skip Extra Prices if <= current price
            if (value.centAmount <= currentPrice.centAmount) return false;

            return true;
        });

    return (
        <Box>
            <Flex direction="column" gap={2}>
                <Flex direction="column" align="flex-start" gap={1.2}>
                    <Money
                        fontWeight="bold"
                        locale={locale}
                        data={currentPrice}
                        color={isProductSalePriceHighlighted(price) ? "red.100" : "black"}
                        data-testid="SelectedVariantPrice"
                        fontSize="desktopH3"
                    />
                    {showGrabPay && (
                        <GrabPayWidget
                            productPrice={currentPrice}
                            locale={locale}
                            productId={productId}
                        />
                    )}
                </Flex>

                <Flex direction="column" gap={2}>
                    {extraPrices.map(({ type, value, key, setKey, discount }) => (
                        <Flex
                            key={type}
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                            color="gray.800"
                        >
                            <Flex gap={1} alignItems="center">
                                {get(setKey, key, {
                                    replacements: {
                                        price: (
                                            <Money
                                                textDecoration={
                                                    discount ? "line-through" : undefined
                                                }
                                                locale={locale}
                                                data-testid={type}
                                                data={value}
                                            />
                                        ),
                                        discount: discount ? (
                                            <Text color="red.100">{`-${discount}%`}</Text>
                                        ) : null,
                                    },
                                    jsx: true,
                                })}
                            </Flex>
                        </Flex>
                    ))}

                    {taxLabel && <Text color="gray.800">{taxLabel}</Text>}
                </Flex>
            </Flex>
        </Box>
    );
};

export default ProductBuyCardPrice;
